import { Client, Session, Socket, MatchData, MatchPresenceEvent, Notification } from "@heroiclabs/nakama-js";
import { prof } from "./Profiler";

// matchデータ opコード（WebSocket sendMatchState 経由の双方向メッセージ）
// MatchLoop のメッセージキューで処理。同一接続内で送信順が保証される。
// RPC と異なり非同期応答（別opコード）で結果を返す。
const OP_INIT_POS          = 1;  // C→S     ログイン時の初期位置・表示名・テクスチャ・loginTime
const OP_MOVE_TARGET       = 2;  // C→S→C   クリック移動の目標位置（AOI内ブロードキャスト）
const OP_AVATAR_CHANGE     = 3;  // C→S→C   アバターテクスチャ変更（AOI内ブロードキャスト）
const OP_BLOCK_UPDATE      = 4;  // C→S→C   ブロック設置/削除（AOI内ブロードキャスト＋Storage保存）
const OP_AOI_UPDATE        = 5;  // C→S     AOI範囲更新（チャンク座標）
const OP_AOI_ENTER         = 6;  // S→C     プレイヤーがAOI内に入った（位置のみ）
const OP_AOI_LEAVE         = 7;  // S→C     プレイヤーがAOI外に出た
const OP_DISPLAY_NAME      = 8;  // C→S→C   表示名変更（全員ブロードキャスト）
const OP_PROFILE_REQUEST   = 9;  // C→S     プロフィール要求（sessionId[]）
const OP_PROFILE_RESPONSE  = 10; // S→C     プロフィール応答（要求者のみ）
const OP_PLAYERS_AOI_REQ   = 11; // C→S     全プレイヤーAOI情報要求
const OP_PLAYERS_AOI_RESP  = 12; // S→C     全プレイヤーAOI情報応答（要求者のみ）
const OP_CHAT              = 13; // C→S→C   チャットメッセージ（全員ブロードキャスト）
const OP_SYSTEM_MSG        = 14; // S→C     システムメッセージ（ログイン/ログアウト通知）
const OP_JUMP              = 15; // C→S→C   アバタージャンプ演出（AOI内ブロードキャスト、ペイロード {}）
const OP_PLAYER_LIST_SUB   = 16; // C→S     プレイヤーリスト購読（{subscribe:true/false}）
const OP_PLAYER_LIST_DATA  = 17; // S→C     全プレイヤーリスト（プッシュ配信）
const OP_OTHELLO_UPDATE    = 18; // S→C     オセロ盤面更新（購読者へ配信）
const OP_OTHELLO_SUB       = 19; // C→S     オセロ購読/解除（{subscribe:true/false}）


/** オセロ盤面更新のペイロード型 */
export interface OthelloUpdatePayload {
    type?: "game";
    gameId: string;
    gameNo?: number;
    board: number[];
    black: string;
    blackName?: string;
    blackUser?: string;
    blackHasGoogle?: boolean;
    blackIsAdmin?: boolean;
    white: string;
    whiteName?: string;
    whiteUser?: string;
    whiteHasGoogle?: boolean;
    whiteIsAdmin?: boolean;
    turn: number;
    status: string;
    lastMove: number;
    winner: number;
    blackCount: number;
    whiteCount: number;
    comment?: string;
    isCpu?: boolean;
    cpuColor?: number;       // CPU 席のビットマスク: 1=黒のみ / 2=白のみ / 3=双方(CPU vs CPU)。isCpu=false のときは 0
    blackCpuName?: string;   // BLACK 席が CPU のときの識別名 (+VE 応答 name 部分)
    whiteCpuName?: string;   // WHITE 席が CPU のときの識別名
    flipped?: number[];
}

/** オセロゲーム一覧のペイロード型
 *  - games は常に存在（現在の待機中＋進行中ゲーム）
 *  - history はゲーム終了通知・初回購読時のみ存在（省略時は履歴更新なし）
 */
export interface OthelloListPayload {
    type: "list";
    games: {
        gameId: string;
        gameNo?: number;
        black: string;
        blackName: string;          // displayName 生値（括弧なし）
        blackUser?: string;         // username（@フォールバック用）
        blackHasGoogle?: boolean;
        blackIsAdmin?: boolean;
        white: string;
        whiteName: string;
        whiteUser?: string;
        whiteHasGoogle?: boolean;
        whiteIsAdmin?: boolean;
        status: string;
        turn: number;
        blackCount: number;
        whiteCount: number;
        comment?: string;
        isCpu?: boolean;
        cpuColor?: number;       // ビットマスク: 1=黒のみ / 2=白のみ / 3=双方。isCpu=false のときは 0
        blackCpuName?: string;   // BLACK 席 CPU 識別名
        whiteCpuName?: string;   // WHITE 席 CPU 識別名
    }[];
    history?: OthelloHistoryRecord[]; // 省略時は履歴更新なし
}

/** オセロ対戦履歴の1レコード */
export interface OthelloHistoryRecord {
    gameId: string;
    gameNo?: number;
    black: string;
    blackName: string;          // displayName 生値（括弧なし）
    blackUser?: string;         // username（@フォールバック用）
    blackHasGoogle?: boolean;
    blackIsAdmin?: boolean;
    white: string;
    whiteName: string;
    whiteUser?: string;
    whiteHasGoogle?: boolean;
    whiteIsAdmin?: boolean;
    blackCount: number;
    whiteCount: number;
    winner: number; // 0=未定, 1=黒勝, 2=白勝, 3=引分
    reason: string; // "normal", "resign", "cancel"
    ts: number;     // UnixMilli
    isCpu?: boolean;
    cpuColor?: number;       // ビットマスク: 1=黒CPU / 2=白CPU / 3=双方CPU / 0=人間のみ
    blackCpuName?: string;   // BLACK 席 CPU 識別名 (+VE 応答 name 部分)
    whiteCpuName?: string;   // WHITE 席 CPU 識別名
}

/** OP_OTHELLO_UPDATE で受信するメッセージの共用型 */
export type OthelloMessage = OthelloUpdatePayload | OthelloListPayload;

export class NakamaService {
    private client: Client;
    private session: Session | null = null;
    private socket: Socket | null = null;
    private matchId: string | null = null;
    private host = location.hostname;
    private port = location.port || (location.protocol === "https:" ? "443" : "80");
    selfSessionId: string | null = null;
    /** サーバ側 Google OAuth エラーコード（0=OK, 1=CLIENT_ID未設定, 2=SECRET未設定, null=未取得） */
    googleOAuthErr: number | null = null;
    /** サーバから取得した Google OAuth Client ID */
    googleClientId: string | null = null;
    /** 現在のマッチに参加中の session ID 一覧 */
    currentPresenceIds: string[] = [];

    get selfMatchId(): string | null { return this.matchId; }

    onChatMessage?: (username: string, text: string, userId: string, sessionId: string, ts: number, hasGoogle?: boolean, isAdmin?: boolean, displayName?: string, nameColor?: string) => void;
    onSystemMessage?: (type: string, username: string, userId: string, sessionId: string, uidCount: number, nameColor: string, ts: number, displayName?: string, hasGoogle?: boolean, isAdmin?: boolean) => void;
    onMatchPresenceJoin?: (sessionId: string, userId: string, username: string) => void;
    onMatchPresenceLeave?: (sessionId: string, userId: string, username: string) => void;
    onAvatarInitPos?:    (sessionId: string, x: number, z: number, ry: number, loginTimeISO: string, displayName: string, textureUrl: string, charCol: number, charRow: number, nameColor?: string) => void;
    onAvatarMoveTarget?: (sessionId: string, x: number, z: number) => void;
    onAvatarChange?:     (sessionId: string, textureUrl: string, charCol: number, charRow: number) => void;
    onAvatarJump?:       (sessionId: string) => void;
    onBlockUpdate?:      (gx: number, gz: number, blockId: number, r: number, g: number, b: number, a: number) => void;
    onAOIEnter?:         (sessionId: string, x: number, z: number, ry: number) => void;
    onAOILeave?:         (sessionId: string) => void;
    onProfileResponse?:  (profiles: { sessionId: string; displayName: string; textureUrl: string; charCol: number; charRow: number; loginTime: string; nameColor?: string; hasGoogle?: boolean; isAdmin?: boolean }[]) => void;
    onPlayersAOIResponse?: (players: { sessionId: string; username: string; minCX: number; minCZ: number; maxCX: number; maxCZ: number; x: number; z: number }[]) => void;
    onDisplayName?:      (sessionId: string, displayName: string, nameColor?: string) => void;
    onPlayerListData?:   (players: { sessionId: string; userId: string; username: string; displayName: string; loginTime: string; nameColor?: string; worldId: number; matchId: string }[]) => void;
    onPlayerListCount?:  (count: number) => void;
    onOthelloUpdate?:    (data: OthelloMessage) => void;
    onNotification?:     (n: Notification) => void;
    onMatchDisconnect?:  () => void;
    onMatchReconnect?:   () => void;
    /** 再接続時に joinMatch に渡すメタデータを取得するコールバック */
    getReconnectMeta?: () => Record<string, string>;

    private matchReadyListeners: (() => void)[] = [];
    private matchReady = false;

    /** マッチ参加完了時に呼ばれるリスナーを登録。既に参加済みなら即座に呼ぶ */
    addMatchReadyListener(fn: () => void): void {
        if (this.matchReady) { fn(); return; }
        this.matchReadyListeners.push(fn);
    }

    private fireMatchReady(): void {
        this.matchReady = true;
        for (const fn of this.matchReadyListeners) fn();
        this.matchReadyListeners.length = 0;
    }

    private reconnecting = false;
    private loginName: string | null = null;
    private loginTimeISO: string = "";
    selfDisplayName: string = "";
    selfNameColor: string = "";
    selfHasGoogle: boolean = false;
    selfIsAdmin: boolean = false;
    private readonly _decoder = new TextDecoder();
    private readonly _encoder = new TextEncoder();

    // onmatchdata プロファイル（呼び出し回数とコスト）
    matchDataProfile = { calls: 0, totalMs: 0, maxMs: 0 };
    private _mdProfileAccum = { calls: 0, totalMs: 0, maxMs: 0, lastReset: performance.now() };

    // op別ハンドラ関数テーブル（name: ログ表示名, silent: 共通ログ抑制, fn: ハンドラ）
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private readonly opHandlers: Record<number, { name: string; silent?: boolean; fn: (payload: any, sid: string | undefined) => void }> = {
        [OP_BLOCK_UPDATE]: { name: "BLOCK_UPDATE", silent: true, fn: (p) => {
            const blk = p as { gx: number; gz: number; blockId: number; r: number; g: number; b: number; a: number };
            this.onBlockUpdate?.(blk.gx, blk.gz, blk.blockId, blk.r ?? 255, blk.g ?? 255, blk.b ?? 255, blk.a ?? 255);
        }},
        [OP_AOI_ENTER]: { name: "AOI_ENTER", silent: true, fn: (p) => {
            type AoiEntry = { sessionId: string; x: number; z: number; ry?: number };
            const entries: AoiEntry[] = Array.isArray(p) ? p : [p];
            for (const e of entries) this.onAOIEnter?.(e.sessionId, e.x, e.z, e.ry ?? 0);
        }},
        [OP_AOI_LEAVE]: { name: "AOI_LEAVE", silent: true, fn: (p) => {
            this.onAOILeave?.((p as { sessionId: string }).sessionId);
        }},
        [OP_PROFILE_RESPONSE]: { name: "PROFILE_RESP", silent: true, fn: (p) => {
            const resp = p as { profiles: { sessionId: string; displayName: string; textureUrl: string; charCol: number; charRow: number; loginTime: string; nameColor?: string; hg?: boolean; ad?: boolean }[] };
            const mapped = (resp.profiles ?? []).map(pr => ({ ...pr, hasGoogle: pr.hg ?? false, isAdmin: pr.ad ?? false }));
            this.onProfileResponse?.(mapped);
        }},
        [OP_PLAYERS_AOI_RESP]: { name: "AOI_RESP", fn: (p) => {
            const resp = p as { players: { sessionId: string; username: string; minCX: number; minCZ: number; maxCX: number; maxCZ: number; x: number; z: number }[] };
            const players = resp.players ?? [];
            if (this._aoiResolve) { this._aoiResolve(players); this._aoiResolve = null; }
            this.onPlayersAOIResponse?.(players);
        }},
        [OP_CHAT]: { name: "CHAT", fn: (p) => {
            const chat = p as { text: string; username: string; userId: string; sessionId: string; ts?: number; hg?: boolean; ad?: boolean; dn?: string; nc?: string };
            this.onChatMessage?.(chat.username ?? "", chat.text ?? "", chat.userId ?? "", chat.sessionId ?? "", chat.ts ?? 0, chat.hg, chat.ad, chat.dn, chat.nc);
        }},
        [OP_SYSTEM_MSG]: { name: "SYS_MSG", fn: (p) => {
            const sys = p as { type: string; username: string; userId: string; sessionId?: string; uidCount?: number; nameColor?: string; ts?: number; displayName?: string; hg?: boolean; ad?: boolean };
            this.onSystemMessage?.(sys.type, sys.username, sys.userId, sys.sessionId ?? "", sys.uidCount ?? 1, sys.nameColor ?? "", sys.ts ?? 0, sys.displayName, sys.hg, sys.ad);
        }},
        [OP_PLAYER_LIST_DATA]: { name: "PL_DATA", fn: (p) => {
            const resp = p as { players?: { sessionId: string; userId: string; username: string; displayName: string; loginTime: string; nameColor?: string; worldId: number; matchId: string }[]; count?: number };
            if (resp.players) this.onPlayerListData?.(resp.players);
            else if (resp.count !== undefined) this.onPlayerListCount?.(resp.count);
        }},
        [OP_OTHELLO_UPDATE]: { name: "OTHELLO", fn: (p) => {
            this.onOthelloUpdate?.(p as OthelloMessage);
        }},
        [OP_DISPLAY_NAME]: { name: "DISPLAY_NAME", silent: true, fn: (p, sid) => {
            if (!sid) return;
            const dn = p as { displayName: string; nc?: string };
            this.onDisplayName?.(sid, dn.displayName, dn.nc);
        }},
        [OP_INIT_POS]: { name: "INIT_POS", silent: true, fn: (p, sid) => {
            if (!sid) return;
            const pos = p as { x: number; z: number; ry?: number; lt?: string; dn?: string; tx?: string; cc?: number; cr?: number; nc?: string };
            this.onAvatarInitPos?.(sid, pos.x, pos.z, pos.ry ?? 0, pos.lt ?? "", pos.dn ?? "", pos.tx ?? "", pos.cc ?? 0, pos.cr ?? 0, pos.nc);
        }},
        [OP_MOVE_TARGET]: { name: "MOVE_TARGET", fn: (p, sid) => {
            if (!sid) return;
            const pos = p as { x: number; z: number };
            this.onAvatarMoveTarget?.(sid, pos.x, pos.z);
        }},
        [OP_AVATAR_CHANGE]: { name: "AVATAR_CHANGE", silent: true, fn: (p, sid) => {
            if (!sid) return;
            const av = p as { textureUrl: string; cc?: number; cr?: number };
            this.onAvatarChange?.(sid, av.textureUrl, av.cc ?? 0, av.cr ?? 0);
        }},
        [OP_JUMP]: { name: "JUMP", silent: true, fn: (_p, sid) => {
            if (!sid) return;
            this.onAvatarJump?.(sid);
        }},
    };

    constructor() {
        const useSSL = location.protocol === "https:";
        this.client = new Client(import.meta.env.VITE_SERVER_KEY ?? "defaultkey", this.host, this.port, useSSL);
    }

    /** Cookie からデバイスIDを取得（Safari↔PWA間の共有用） */
    private getDeviceIdCookie(loginName: string): string | null {
        const ckey = `nakama_did_${loginName}`;
        const match = document.cookie.match(new RegExp("(?:^|; )" + ckey + "=([^;]*)"));
        return match ? decodeURIComponent(match[1]) : null;
    }

    /** Cookie にデバイスIDを保存（Safari→PWA引き継ぎ用、SameSite=Strict） */
    private setDeviceIdCookie(loginName: string, deviceId: string): void {
        const ckey = `nakama_did_${loginName}`;
        const secure = location.protocol === "https:" ? ";Secure" : "";
        document.cookie = `${ckey}=${encodeURIComponent(deviceId)};path=/;max-age=3600;SameSite=Strict${secure}`;
    }

    /** Cookie からデバイスIDを削除（引き継ぎ完了後） */
    private deleteDeviceIdCookie(loginName: string): void {
        const ckey = `nakama_did_${loginName}`;
        document.cookie = `${ckey}=;path=/;max-age=0`;
    }

    private generateDeviceId(): string {
        return (typeof crypto.randomUUID === "function")
            ? crypto.randomUUID()
            : (([1e7] as unknown as string) + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c: string) =>
                (+c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> +c / 4).toString(16));
    }

    /** User-Agent からプラットフォームを判定する */
    static detectPlatform(): string {
        const ua = navigator.userAgent;
        if (/iPhone/.test(ua)) return "iPhone";
        if (/iPad/.test(ua)) return "iPad";
        // iPadOS 13+ / 将来の iOS はタッチデバイスでも Mac と同じ UA を返す
        // maxTouchPoints で iOS 端末と判定し、画面の短辺 < 600px なら iPhone、それ以上なら iPad
        if (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1) {
            const minDim = Math.min(screen.width, screen.height);
            return minDim < 600 ? "iPhone" : "iPad";
        }
        if (/Android/.test(ua)) return "Android";
        if (/Windows NT/.test(ua)) return "Windows";
        if (/Macintosh/.test(ua)) return "Mac";
        return "その他";
    }

    /** 現在のセッションで使用中のデバイス ID を返す（未ログインなら空文字） */
    getCurrentDeviceId(): string {
        if (!this.loginName) return "";
        return localStorage.getItem(`nakama_device_id_${this.loginName}`) ?? "";
    }

    private getOrCreateDeviceId(loginName: string): string {
        const key = `nakama_device_id_${loginName}`;
        // 1. localStorage（同一コンテキスト内で最速）
        let deviceId = localStorage.getItem(key);
        if (deviceId) {
            // Cookie にも同期（Safari→PWA引き継ぎ用）
            this.setDeviceIdCookie(loginName, deviceId);
            return deviceId;
        }
        // 2. Cookie（Safari↔PWA間の共有フォールバック）
        const cookieValue = this.getDeviceIdCookie(loginName);
        if (cookieValue) {
            localStorage.setItem(key, cookieValue);
            this.deleteDeviceIdCookie(loginName);  // 引き継ぎ完了、Cookie削除
            console.log("snd DeviceId restored from cookie (PWA bridge)");
            return cookieValue;
        }
        // 3. 新規生成 → localStorage + Cookie に保存
        deviceId = this.generateDeviceId();
        localStorage.setItem(key, deviceId);
        this.setDeviceIdCookie(loginName, deviceId);
        return deviceId;
    }

    async login(loginName: string): Promise<Session> {
        const _end = prof("NakamaService.login");
        try {
        this.loginName = loginName;
        const useSSL = location.protocol === "https:";
        const key = import.meta.env.VITE_SERVER_KEY || "defaultkey";
        console.log(`snd Connect ${useSSL ? "https" : "http"}://${this.host}:${this.port} (SSL=${useSSL})`);
        this.client = new Client(key, this.host, this.port, useSSL);
        const deviceId = this.getOrCreateDeviceId(loginName);
        this.session = await this.client.authenticateDevice(deviceId, true);
        // デバイス認証後にusernameを設定し、セッションを再取得（JWTにusernameを反映）
        if (this.session.username !== loginName) {
            try {
                await this.client.updateAccount(this.session, { username: loginName });
            } catch {
                const hasLocal = !!localStorage.getItem(`nakama_device_id_${loginName}`);
                const hasCookie = !!this.getDeviceIdCookie(loginName);
                const isPWA = window.matchMedia("(display-mode: standalone)").matches;
                throw new Error(
                    `ユーザーID "${loginName}" は既に使用されています。\n` +
                    `原因: このブラウザに紐付くデバイスIDがサーバー上の "${loginName}" と一致しません。\n` +
                    `状態: localStorage=${hasLocal ? "あり" : "なし"}, Cookie=${hasCookie ? "あり" : "なし"}, PWA=${isPWA ? "はい" : "いいえ"}\n` +
                    `対処: メニュー→「クッキー初期化」を実行するか、別のユーザーIDでログインしてください。`
                );
            }
            this.session = await this.client.authenticateDevice(deviceId, false);
        }
        console.log("snd Login username:", this.session.username);

        this.socket = this.client.createSocket(useSSL, false);
        this.socket.setHeartbeatTimeoutMs(60000);
        await this.socket.connect(this.session, true);

        this.setupSocketHandlers();

        this.selfSessionId = this.session.user_id ? (this.session as unknown as { session_id?: string }).session_id ?? "" : "";
        this.loginTimeISO = new Date().toISOString();

        // デバイスのプラットフォーム情報をサーバに送信（非同期・失敗しても続行）
        this.socket.rpc("registerDeviceInfo", JSON.stringify({
            deviceId,
            platform: NakamaService.detectPlatform(),
        })).catch(e => console.warn("registerDeviceInfo failed:", e));

        return this.session;
        } finally { _end(); }
    }

    private setupSocketHandlers(): void {
        const _end = prof("NakamaService.setupSocketHandlers");
        if (!this.socket) { _end(); return; }
        this.socket.ondisconnect = (evt: Event) => {
            // Nakama SDK は disconnect() 呼び出しと WebSocket onClose で二重発火するためガード
            if (this.reconnecting) return;
            const ce = evt as CloseEvent;
            console.warn(`NakamaService WebSocket disconnected code=${ce.code ?? "?"} reason=${ce.reason ?? ""} wasClean=${ce.wasClean ?? "?"}`);
            this.onMatchDisconnect?.();
            this.tryReconnect();
        };
        this.socket.onnotification = (n: Notification) => {
            console.log(`rcv notification code=${n.code ?? "?"} id=${n.id ?? "?"}`);
            this.onNotification?.(n);
        };
        _end();
    }

    /** DB 永続化された通知を取得（ログイン直後の取り残し回収用、仕様書 docs/20 参照） */
    async fetchPendingNotifications(limit = 100): Promise<Notification[]> {
        if (!this.session) return [];
        try {
            const list = await this.client.listNotifications(this.session, limit);
            return list.notifications ?? [];
        } catch (e) {
            console.warn("listNotifications failed:", e);
            return [];
        }
    }

    /** 指定 ID の通知を DB から削除（処理後の重複排除用） */
    async deleteNotifications(ids: string[]): Promise<void> {
        if (!this.session || ids.length === 0) return;
        try {
            await this.client.deleteNotifications(this.session, ids);
        } catch (e) {
            console.warn("deleteNotifications failed:", e);
        }
    }

    private async tryReconnect(): Promise<void> {
        const _end = prof("NakamaService.tryReconnect");
        try {
        if (this.reconnecting || !this.session || !this.loginName) return;
        this.reconnecting = true;
        const delays = [1000, 2000, 4000, 8000, 15000];
        for (let attempt = 0; attempt < delays.length; attempt++) {
            await new Promise(r => setTimeout(r, delays[attempt]));
            if (!this.session) { this.reconnecting = false; return; } // logged out
            try {
                console.log(`NakamaService reconnect attempt ${attempt + 1}/${delays.length}`);
                this.socket = this.client.createSocket(location.protocol === "https:", false);
                this.socket.setHeartbeatTimeoutMs(60000);
                await this.socket.connect(this.session, true);
                this.setupSocketHandlers();
                // マッチ再参加（チャットもマッチ経由、メタデータ付き）
                const meta = this.getReconnectMeta?.() ?? {};
                await this.joinWorldMatch(meta);
                console.log("NakamaService reconnected successfully");
                this.onMatchReconnect?.();
                this.reconnecting = false;
                return;
            } catch (e) {
                console.warn(`NakamaService reconnect attempt ${attempt + 1} failed:`, e);
            }
        }
        console.error("NakamaService all reconnect attempts failed");
        this.reconnecting = false;
        } finally { _end(); }
    }

    async joinWorldMatch(initMeta?: Record<string, string>, worldId = 0): Promise<{ worldId: number; chunkCountX: number; chunkCountZ: number }> {
        const _end = prof("NakamaService.joinWorldMatch");
        try {
        console.log(`snd getWorldMatch worldId=${worldId}`);
        if (!this.session || !this.socket) throw new Error("no session/socket");
        const result = await this.socket.rpc("getWorldMatch", JSON.stringify({ worldId }));
        if (!result?.payload) throw new Error("getWorldMatch: no payload");
        const data = JSON.parse(result.payload) as { matchId?: string; worldId?: number; chunkCountX?: number; chunkCountZ?: number; displayName?: string };
        if (!data.matchId) throw new Error("getWorldMatch: no matchId");
        this.matchId = data.matchId;
        // サーバ DB の display_name を優先採用（別デバイスでも同じ Google UID なら引き継がれる）
        if (data.displayName && data.displayName !== this.selfDisplayName) {
            console.log(`joinWorldMatch: adopting server displayName=${data.displayName} (was ${this.selfDisplayName})`);
            this.selfDisplayName = data.displayName;
        }
        const worldInfo = { worldId: data.worldId ?? 0, chunkCountX: data.chunkCountX ?? 64, chunkCountZ: data.chunkCountZ ?? 64 };

        // joinMatch() より前にハンドラを登録する（MatchJoin直後のサーバー通知を取りこぼさないため）
        this.socket.onmatchdata = (md: MatchData) => {
            const _mt0 = performance.now();
            const sid = md.presence?.session_id;
            const op = md.op_code;
            const entry = this.opHandlers[op];
            if (!entry?.silent) {
                console.log(`rcv op=${op} ${entry?.name ?? `?${op}`} sid=${sid ? sid.slice(0, 8) : "(srv)"}`);
            }
            try {
                const payload = JSON.parse(this._decoder.decode(md.data));
                if (entry) entry.fn(payload, sid);
            } catch (e) {
                console.warn(`rcv parse error op=${op} ${entry?.name ?? `?${op}`}`, e);
            }
            // プロファイル集計（1秒ごとにリセット）
            const _mt1 = performance.now();
            const elapsed = _mt1 - _mt0;
            const acc = this._mdProfileAccum;
            acc.calls++;
            acc.totalMs += elapsed;
            if (elapsed > acc.maxMs) acc.maxMs = elapsed;
            if (_mt1 - acc.lastReset >= 1000) {
                this.matchDataProfile = { calls: acc.calls, totalMs: acc.totalMs, maxMs: acc.maxMs };
                acc.calls = acc.totalMs = acc.maxMs = 0;
                acc.lastReset = _mt1;
            }
        };
        // onmatchpresence を joinMatch より前に登録（MatchLeave の取りこぼし防止）
        this.socket.onmatchpresence = (event: MatchPresenceEvent) => {
            for (const p of event.joins ?? []) this.onMatchPresenceJoin?.(p.session_id, p.user_id, p.username);
            for (const p of event.leaves ?? []) this.onMatchPresenceLeave?.(p.session_id, p.user_id, p.username);
        };
        console.log(`snd joinMatch matchId=${this.matchId.slice(0,8)} meta=${JSON.stringify(initMeta)}`);
        const match = await this.socket.joinMatch(this.matchId, undefined, initMeta ?? {});
        // selfSessionId を確定 + 現在の presences を記録
        const ids: string[] = [];
        if (match.self) {
            this.selfSessionId = match.self.session_id;
            ids.push(match.self.session_id);
            this.onMatchPresenceJoin?.(match.self.session_id, match.self.user_id, match.self.username);
        }
        for (const p of match.presences ?? []) {
            ids.push(p.session_id);
            this.onMatchPresenceJoin?.(p.session_id, p.user_id, p.username);
        }
        this.currentPresenceIds = ids;
        this.fireMatchReady();
        return worldInfo;
        } finally { _end(); }
    }

    /** 現在のマッチから離脱 */
    async leaveMatch(): Promise<void> {
        if (!this.socket || !this.matchId) return;
        console.log(`snd leaveMatch matchId=${this.matchId.slice(0, 8)}`);
        await this.socket.leaveMatch(this.matchId);
        this.matchId = undefined as unknown as string;
    }

    /** ワールド切替中フラグ（leave→join の間、ログアウト通知を抑制） */
    changingWorld = false;

    /** ワールド切替: 新マッチ join（metadata に worldMove=1）→ 旧マッチ leave */
    async changeWorldMatch(worldId: number, initMeta?: Record<string, string>): Promise<{ worldId: number; chunkCountX: number; chunkCountZ: number }> {
        this.changingWorld = true;
        const oldMatchId = this.matchId;
        // 先に新マッチに join（metadata で worldMove フラグを渡す）
        // MatchJoinAttempt で worldMovingUsers にセットされてから旧マッチの MatchLeave が呼ばれる
        const meta = { ...initMeta, worldMove: "1" };
        const result = await this.joinWorldMatch(meta, worldId);
        // 旧マッチを leave
        if (oldMatchId && this.socket) {
            await this.socket.leaveMatch(oldMatchId);
        }
        this.changingWorld = false;
        return result;
    }

    async sendInitPos(x: number, z: number, ry = 0, textureUrl = "", charCol = 0, charRow = 0): Promise<void> {
        const _end = prof("NakamaService.sendInitPos");
        try {
        console.log(`snd initPos x=${(+x).toFixed(1)} z=${(+z).toFixed(1)} ry=${(+ry).toFixed(1)} tx=${textureUrl} cc=${charCol} cr=${charRow}`);
        if (!this.socket || !this.matchId) return;
        try {
            await this.socket.sendMatchState(this.matchId, OP_INIT_POS, this._encoder.encode(JSON.stringify({ x, z, ry, lt: this.loginTimeISO, dn: this.selfDisplayName, tx: textureUrl, cc: charCol, cr: charRow, nc: this.selfNameColor })));
        } catch (e) { console.warn("NakamaService:", e); }
        } finally { _end(); }
    }

    async sendAvatarChange(textureUrl: string, charCol = 0, charRow = 0): Promise<void> {
        const _end = prof("NakamaService.sendAvatarChange");
        try {
        console.log(`snd sendAvatarChange textureUrl=${textureUrl} cc=${charCol} cr=${charRow}`);
        if (!this.socket || !this.matchId) return;
        try {
            await this.socket.sendMatchState(this.matchId, OP_AVATAR_CHANGE, this._encoder.encode(JSON.stringify({ textureUrl, cc: charCol, cr: charRow })));
        } catch (e) { console.warn("NakamaService:", e); }
        } finally { _end(); }
    }

    async sendDisplayName(displayName: string): Promise<void> {
        const _end = prof("NakamaService.sendDisplayName");
        try {
        console.log(`snd sendDisplayName displayName=${displayName}`);
        if (!this.socket || !this.matchId) return;
        try {
            await this.socket.sendMatchState(this.matchId, OP_DISPLAY_NAME, this._encoder.encode(JSON.stringify({ displayName, nc: this.selfNameColor })));
        } catch (e) { console.warn("NakamaService:", e); }
        } finally { _end(); }
    }

    async sendNameColor(nameColor: string): Promise<void> {
        if (!this.socket || !this.matchId) return;
        this.selfNameColor = nameColor;
        try {
            await this.socket.sendMatchState(this.matchId, OP_DISPLAY_NAME, this._encoder.encode(JSON.stringify({ displayName: this.selfDisplayName, nc: nameColor })));
        } catch (e) { console.warn("NakamaService:", e); }
    }

    async sendJump(): Promise<void> {
        if (!this.socket || !this.matchId) return;
        try {
            await this.socket.sendMatchState(this.matchId, OP_JUMP, JSON.stringify({}));
        } catch (e) { console.warn("NakamaService:", e); }
    }

    async sendMoveTarget(x: number, z: number): Promise<void> {
        const _end = prof("NakamaService.sendMoveTarget");
        try {
        if (!this.socket || !this.matchId) return;
        try {
            await this.socket.sendMatchState(this.matchId, OP_MOVE_TARGET, JSON.stringify({ x, z }));
        } catch (e) { console.warn("NakamaService:", e); }
        } finally { _end(); }
    }

    async sendAOI(minCX: number, minCZ: number, maxCX: number, maxCZ: number): Promise<void> {
        const _end = prof("NakamaService.sendAOI");
        try {
        if (!this.socket || !this.matchId) return;
        try {
            await this.socket.sendMatchState(this.matchId, OP_AOI_UPDATE, JSON.stringify({ minCX, minCZ, maxCX, maxCZ }));
        } catch (e) { console.warn("NakamaService:", e); }
        } finally { _end(); }
    }

    async sendProfileRequest(sessionIds: string[]): Promise<void> {
        const _end = prof("NakamaService.sendProfileRequest");
        try {
        console.log(`snd profileRequest sids=${sessionIds.map(s => s.slice(0, 8)).join(",")}`);
        if (!this.socket || !this.matchId || sessionIds.length === 0) return;
        try {
            await this.socket.sendMatchState(this.matchId, OP_PROFILE_REQUEST, this._encoder.encode(JSON.stringify({ sessionIds })));
        } catch (e) { console.warn("NakamaService:", e); }
        } finally { _end(); }
    }

    async sendChatMessage(text: string): Promise<void> {
        const _end = prof("NakamaService.sendChatMessage");
        try {
        console.log(`snd sendChatMessage text=${text}`);
        if (!this.socket || !this.matchId) return;
        await this.socket.sendMatchState(this.matchId, OP_CHAT, this._encoder.encode(JSON.stringify({ text })));
        } finally { _end(); }
    }

    logout(): void {
        const _end = prof("NakamaService.logout");
        console.log("snd logout");
        if (this.socket) {
            this.socket.disconnect(true);
            this.socket = null;
        }
        this.session       = null;
        this.matchId       = null;
        this.selfSessionId = null;
        _end();
    }

    getSession(): Session | null {
        return this.session;
    }

    async updateDisplayName(displayName: string): Promise<void> {
        const _end = prof("NakamaService.updateDisplayName");
        try {
        console.log(`snd updateDisplayName displayName=${displayName}`);
        if (!this.socket) throw new Error("no socket");
        await this.socket.rpc("updateDisplayName", JSON.stringify({ displayName }));
        } finally { _end(); }
    }

    /**
     * Google OAuth2 認可コードを使って現在のアカウントに Google を紐付ける。
     * docs/53-設計-認証システム.md §13 参照。
     * @param code         クライアント側で取得した OAuth2 認可コード
     * @param redirectUri  認可リクエストで使用した redirect_uri と完全一致させる
     * @returns linked=true なら紐付け成功、alreadyLinked=true なら別ユーザーが既にリンク済み
     */
    async linkGoogleByCode(code: string, redirectUri: string): Promise<{ linked: boolean; alreadyLinked?: boolean; token?: string; displayName?: string }> {
        const _end = prof("NakamaService.linkGoogleByCode");
        try {
            console.log("snd linkGoogleByCode");
            if (!this.socket) throw new Error("no socket");
            const r = await this.socket.rpc("linkGoogleByCode", JSON.stringify({ code, redirectUri }));
            if (r?.payload) {
                const data = JSON.parse(r.payload) as { linked?: boolean; alreadyLinked?: boolean; token?: string; displayName?: string };
                if (data.alreadyLinked && data.token) {
                    return { linked: false, alreadyLinked: true, token: data.token, displayName: data.displayName };
                }
                if (!data.linked) throw new Error("link failed");
            }
            return { linked: true };
        } finally { _end(); }
    }

    /** Google アカウントの紐付けを解除する。 */
    async unlinkGoogle(): Promise<void> {
        const _end = prof("NakamaService.unlinkGoogle");
        try {
            console.log("snd unlinkGoogle");
            if (!this.socket) throw new Error("no socket");
            await this.socket.rpc("unlinkGoogle");
        } finally { _end(); }
    }

    /** このデバイスをアカウントからアンリンクする（サーバ側デバイス情報も削除） */
    async detachDevice(): Promise<void> {
        const _end = prof("NakamaService.detachDevice");
        try {
            const deviceId = this.getCurrentDeviceId();
            if (!this.socket) throw new Error("no socket");
            await this.socket.rpc("detachDevice", JSON.stringify({ deviceId }));
        } finally { _end(); }
    }

    /**
     * サーバが発行したセッショントークンを使って既存アカウントに切り替える。
     * 現在のセッション（デバイス認証）を破棄し、Google 紐付き済みユーザーとして再接続する。
     * @param token サーバ側 AuthenticateTokenGenerate で生成されたセッション JWT
     */
    async switchToGoogleAccount(token: string): Promise<void> {
        const _end = prof("NakamaService.switchToGoogleAccount");
        try {
            console.log("snd switchToGoogleAccount");
            // 現在のソケットを切断（再接続ループを防止）
            this.reconnecting = true;
            if (this.socket) {
                this.socket.disconnect(true);
                this.socket = null;
            }
            this.matchId = null;
            this.selfSessionId = null;

            // サーバ発行トークンからセッションを復元
            this.session = Session.restore(token, "");
            const newUsername = this.session.username ?? "";
            console.log("snd switchToGoogleAccount: restored session as", newUsername);

            // このデバイスを Google アカウントにリンク（リロード後も同じアカウントに戻れるようにする）
            const newDeviceId = this.getOrCreateDeviceId(newUsername);
            try {
                await this.client.linkDevice(this.session, { id: newDeviceId });
                console.log("snd switchToGoogleAccount: linked device for", newUsername);
            } catch (e) {
                // "already in use" は既にリンク済み → 問題なし
                const msg = e instanceof Error ? e.message : String(e);
                if (!msg.includes("already")) {
                    console.warn("switchToGoogleAccount: linkDevice failed:", e);
                }
            }
            // loginName を更新（Cookie 更新は UIPanel 側で行う）
            this.loginName = newUsername;

            // WebSocket 再接続
            const useSSL = location.protocol === "https:";
            this.socket = this.client.createSocket(useSSL, false);
            this.socket.setHeartbeatTimeoutMs(60000);
            await this.socket.connect(this.session, true);
            this.setupSocketHandlers();

            this.selfSessionId = (this.session as unknown as { session_id?: string }).session_id ?? "";
            this.reconnecting = false;

            // デバイスのプラットフォーム情報をサーバに送信（非同期・失敗しても続行）
            this.socket.rpc("registerDeviceInfo", JSON.stringify({
                deviceId: newDeviceId,
                platform: NakamaService.detectPlatform(),
            })).catch(e => console.warn("registerDeviceInfo failed (switchToGoogleAccount):", e));

            // マッチ再参加
            const meta = this.getReconnectMeta?.() ?? {};
            await this.joinWorldMatch(meta);
            console.log("snd switchToGoogleAccount: rejoined match");
            this.onMatchReconnect?.();
        } catch (e) {
            this.reconnecting = false;
            throw e;
        } finally { _end(); }
    }

    /**
     * 現在のアカウントが「保存済み」(Google/Apple/Email のいずれかにリンク済み) かを取得する。
     */
    async getAccountStatus(): Promise<{ saved: boolean; hasGoogle: boolean; hasApple: boolean; hasEmail: boolean; hasDevice: boolean; email: string; devices: string[]; devicePlatforms: Record<string, string>; isAdmin: boolean }> {
        const _end = prof("NakamaService.getAccountStatus");
        try {
            const empty = { saved: false, hasGoogle: false, hasApple: false, hasEmail: false, hasDevice: false, email: "", devices: [] as string[], devicePlatforms: {} as Record<string, string>, isAdmin: false };
            if (!this.socket) return empty;
            const r = await this.socket.rpc("getAccountStatus");
            if (r?.payload) {
                const d = JSON.parse(r.payload) as Partial<typeof empty>;
                const dp = d.devicePlatforms;
                return {
                    saved:     d.saved     ?? false,
                    hasGoogle: d.hasGoogle ?? false,
                    hasApple:  d.hasApple  ?? false,
                    hasEmail:  d.hasEmail  ?? false,
                    hasDevice: d.hasDevice ?? false,
                    email:     d.email     ?? "",
                    devices:   Array.isArray(d.devices) ? d.devices : [],
                    devicePlatforms: (dp && typeof dp === "object" && !Array.isArray(dp)) ? dp : {},
                    isAdmin:   d.isAdmin   ?? false,
                };
            }
            return empty;
        } finally { _end(); }
    }

    /** 初回ログイン時の初期アバターインデックスを取得（サーバ側の累計ログインカウンターに基づく）。
     *  2回目以降は同じ値を返す。取得失敗時は null。 */
    async getInitialAvatarIndex(): Promise<number | null> {
        if (!this.socket) return null;
        try {
            const r = await this.socket.rpc("getInitialAvatarIndex");
            if (r?.payload) {
                const d = JSON.parse(r.payload) as { index?: number };
                if (typeof d.index === "number") return d.index;
            }
        } catch (e) {
            console.warn("NakamaService.getInitialAvatarIndex:", e);
        }
        return null;
    }

    async getDisplayNames(userIds: string[]): Promise<Map<string, { displayName: string; hasGoogle: boolean; isAdmin: boolean }>> {
        const _end = prof("NakamaService.getDisplayNames");
        try {
        const result = new Map<string, { displayName: string; hasGoogle: boolean; isAdmin: boolean }>();
        if (!this.socket || userIds.length === 0) return result;
        const rpcResult = await this.socket.rpc("getDisplayNames", JSON.stringify({ userIds }));
        if (rpcResult?.payload) {
            const data = JSON.parse(rpcResult.payload) as { users?: { id: string; displayName: string; hg?: boolean; ad?: boolean }[] };
            for (const u of data.users ?? []) {
                result.set(u.id, { displayName: u.displayName, hasGoogle: u.hg ?? false, isAdmin: u.ad ?? false });
            }
        }
        return result;
        } finally { _end(); }
    }

    /** ワールド一覧を取得 */
    async getWorldList(): Promise<{ worlds: { id: number; name: string; chunkCountX: number; chunkCountZ: number; ownerUid: string; ownerName: string; playerCount: number }[]; isAdmin: boolean }> {
        if (!this.socket) return { worlds: [], isAdmin: false };
        const r = await this.socket.rpc("getWorldList");
        if (r?.payload) {
            const data = JSON.parse(r.payload) as { worlds?: { id: number; name: string; chunkCountX: number; chunkCountZ: number; ownerUid: string; ownerName: string; playerCount: number }[]; isAdmin?: boolean };
            return { worlds: data.worlds ?? [], isAdmin: data.isAdmin ?? false };
        }
        return { worlds: [], isAdmin: false };
    }

    /** 部屋を作成 */
    async createRoom(name: string, chunkCountX: number, chunkCountZ: number): Promise<number> {
        if (!this.socket) throw new Error("no socket");
        const r = await this.socket.rpc("createRoom", JSON.stringify({ name, chunkCountX, chunkCountZ }));
        if (r?.payload) {
            const data = JSON.parse(r.payload) as { worldId?: number };
            return data.worldId ?? -1;
        }
        return -1;
    }

    /** 部屋を削除 */
    async deleteRoom(worldId: number): Promise<boolean> {
        if (!this.socket) return false;
        const r = await this.socket.rpc("deleteRoom", JSON.stringify({ worldId }));
        if (r?.payload) {
            const data = JSON.parse(r.payload) as { deleted?: boolean };
            return data.deleted ?? false;
        }
        return false;
    }

    /** サーバーからブックマーク一覧を取得 */
    async getBookmarks(): Promise<{ name: string; x: number; z: number; ry: number; worldId: number }[]> {
        if (!this.socket) return [];
        const rpcResult = await this.socket.rpc("getBookmarks");
        if (rpcResult?.payload) {
            const data = JSON.parse(rpcResult.payload) as { items?: { name: string; x: number; z: number; ry: number; worldId: number }[] };
            return data.items ?? [];
        }
        return [];
    }

    /** ブックマーク一覧をサーバーに保存 */
    async saveBookmarks(items: { name: string; x: number; z: number; ry: number; worldId: number }[]): Promise<void> {
        if (!this.socket) return;
        await this.socket.rpc("saveBookmarks", JSON.stringify({ items }));
    }

    async getServerInfo(): Promise<string> {
        const _end = prof("NakamaService.getServerInfo");
        try {
        if (!this.socket) return "不明";
        console.log("snd getServerInfo");
        // ① カスタム RPC "getServerInfo" (WebSocket経由)
        try {
            const result = await this.socket.rpc("getServerInfo");
            if (result?.payload) {
                const data = JSON.parse(result.payload) as { name?: string; version?: string; pluginDate?: string; pluginCommit?: string; googleOAuthErr?: number; googleClientId?: string };
                if (typeof data.googleOAuthErr === "number") this.googleOAuthErr = data.googleOAuthErr;
                if (data.googleClientId) this.googleClientId = data.googleClientId;
                const parts: string[] = [];
                if (data.name || data.version)
                    parts.push(`NakamaServerName="${[data.name, data.version ? `v${data.version}` : ""].filter(Boolean).join(" ")}"`);
                if (data.pluginDate || data.pluginCommit) {
                    const date = data.pluginDate?.replace(/_/g, " ") ?? "";
                    const commit = data.pluginCommit ?? "";
                    parts.push(`world.so ${[date, commit].filter(Boolean).join(" ")}`);
                }
                if (parts.length) return parts.join(" ");
            }
        } catch (e) { console.warn("NakamaService.getServerInfo RPC fallback:", e); }
        // ② /v2/serverinfo (Nakama 3.x+) — 同一オリジン経由
        const base = location.origin;
        try {
            const res = await fetch(`${base}/v2/serverinfo`, {
                headers: { "Authorization": `Bearer ${this.session?.token}` }
            });
            const text = await res.text();
            if (res.ok && text) {
                const data = JSON.parse(text) as { version?: string; name?: string };
                const parts = [data.name, data.version].filter(Boolean);
                if (parts.length) return parts.join(" ");
            }
        } catch (e) { console.warn("NakamaService:", e); }
        return "不明";
        } finally { _end(); }
    }

    async setBlock(gx: number, gz: number, blockId: number, r: number, g: number, b: number, a = 255): Promise<void> {
        const _end = prof("NakamaService.setBlock");
        try {
        console.log(`snd setBlock gx=${gx} gz=${gz} blockId=${blockId} rgba=(${r},${g},${b},${a})`);
        if (!this.socket || !this.matchId) return;
        await this.socket.sendMatchState(this.matchId, OP_BLOCK_UPDATE, JSON.stringify({ gx, gz, blockId, r, g, b, a }));
        } finally { _end(); }
    }

    async getGroundTable(): Promise<number[] | null> {
        if (!this.socket) return null;
        console.log("snd getGroundTable");
        try {
            const result = await this.socket.rpc("getGroundTable");
            if (!result?.payload) return null;
            const data = JSON.parse(result.payload) as { table?: number[] };
            return data.table ?? null;
        } catch (e) { console.warn("NakamaService.getGroundTable:", e); return null; }
    }

    async syncChunks(minCX: number, minCZ: number, maxCX: number, maxCZ: number, hashes: Record<string, string>, worldId = 0): Promise<{ cx: number; cz: number; hash: string; table: number[] }[]> {
        const _end = prof("NakamaService.syncChunks");
        try {
        if (!this.socket) return [];
        console.log(`snd syncChunks (${minCX},${minCZ})-(${maxCX},${maxCZ}) w=${worldId}`);
        try {
            const result = await this.socket.rpc("syncChunks", JSON.stringify({ worldId, minCX, minCZ, maxCX, maxCZ, hashes }));
            if (!result?.payload) return [];
            const data = JSON.parse(result.payload) as { chunks?: { cx: number; cz: number; hash: string; table: number[] }[] };
            return data.chunks ?? [];
        } catch (e) { console.warn("NakamaService.syncChunks:", e); return []; }
        } finally { _end(); }
    }

    async getGroundChunk(cx: number, cz: number, worldId = 0): Promise<{ cx: number; cz: number; table: number[] } | null> {
        const _end = prof("NakamaService.getGroundChunk");
        try {
        console.log(`snd getGroundChunk cx=${cx} cz=${cz} w=${worldId}`);
        if (!this.socket) return null;
        try {
            const result = await this.socket.rpc("getGroundChunk", JSON.stringify({ worldId, cx, cz }));
            if (!result?.payload) return null;
            return JSON.parse(result.payload) as { cx: number; cz: number; table: number[] };
        } catch (e) { console.warn("NakamaService.getGroundChunk:", e); return null; }
        } finally { _end(); }
    }

    // サーバへの WebSocket RPC ラウンドトリップ時間を計測する（ms）
    async measurePing(): Promise<number | null> {
        const _end = prof("NakamaService.measurePing");
        try {
        if (!this.socket) return null;
        try {
            const t0 = performance.now();
            await this.socket.rpc("ping");
            return Math.round(performance.now() - t0);
        } catch (e) {
            console.warn("NakamaService.measurePing:", e);
            return null;
        }
        } finally { _end(); }
    }

    // サーバの同接数を取得する（range指定で履歴も取得）
    async getPlayerCount(range?: string): Promise<{ count: number; history: number[] } | null> {
        const _end = prof("NakamaService.getPlayerCount");
        try {
        console.log(`snd getPlayerCount range=${range ?? ""}`);
        if (!this.socket) return null;
        try {
            const payload = range ? JSON.stringify({ range }) : undefined;
            const result = await this.socket.rpc("getPlayerCount", payload);
            if (!result?.payload) return null;
            const data = JSON.parse(result.payload) as { count?: number; history?: number[] };
            return { count: data.count ?? 0, history: data.history ?? [] };
        } catch (e) {
            console.warn("NakamaService.getPlayerCount:", e);
            return null;
        }
        } finally { _end(); }
    }

    // 指定ワールドの最新チャット履歴（最大 20 件）を取得
    async getRecentChat(worldId: number): Promise<{ username: string; text: string; userId: string; sessionId: string; ts: number; hasGoogle?: boolean; isAdmin?: boolean; displayName?: string; nameColor?: string }[]> {
        const _end = prof("NakamaService.getRecentChat");
        try {
            if (!this.socket) return [];
            try {
                const result = await this.socket.rpc("getRecentChat", JSON.stringify({ worldId }));
                if (!result?.payload) return [];
                const data = JSON.parse(result.payload) as { messages?: { username?: string; text?: string; userId?: string; sessionId?: string; ts?: number; hg?: boolean; ad?: boolean; dn?: string; nc?: string }[] };
                return (data.messages ?? []).map(m => ({
                    username: m.username ?? "",
                    text: m.text ?? "",
                    userId: m.userId ?? "",
                    sessionId: m.sessionId ?? "",
                    ts: m.ts ?? 0,
                    hasGoogle: m.hg,
                    isAdmin: m.ad,
                    displayName: m.dn,
                    nameColor: m.nc,
                }));
            } catch (e) {
                console.warn("NakamaService.getRecentChat:", e);
                return [];
            }
        } finally { _end(); }
    }

    // プレイヤーリストのプッシュ配信を購読/解除
    async subscribePlayerList(subscribe: boolean, mode: "count" | "full" = "full"): Promise<void> {
        if (!this.socket || !this.matchId) return;
        try {
            await this.socket.sendMatchState(this.matchId, OP_PLAYER_LIST_SUB, this._encoder.encode(JSON.stringify({ subscribe, mode })));
        } catch (e) { console.warn("NakamaService.subscribePlayerList:", e); }
    }

    // 全プレイヤーのAOI情報を取得（matchデータ方式: 要求→応答）
    private _aoiResolve: ((players: { sessionId: string; username: string; minCX: number; minCZ: number; maxCX: number; maxCZ: number; x: number; z: number }[]) => void) | null = null;
    async getPlayersAOI(): Promise<{ sessionId: string; username: string; minCX: number; minCZ: number; maxCX: number; maxCZ: number; x: number; z: number }[]> {
        const _end = prof("NakamaService.getPlayersAOI");
        try {
        console.log("snd getPlayersAOI");
        if (!this.socket || !this.matchId) return [];
        const promise = new Promise<{ sessionId: string; username: string; minCX: number; minCZ: number; maxCX: number; maxCZ: number; x: number; z: number }[]>((resolve) => {
            this._aoiResolve = resolve;
            setTimeout(() => { if (this._aoiResolve === resolve) { this._aoiResolve = null; resolve([]); } }, 3000);
        });
        await this.socket.sendMatchState(this.matchId, OP_PLAYERS_AOI_REQ, "{}");
        return promise;
        } finally { _end(); }
    }

    /** サーバ側プロファイル RPC を呼び出す */
    async profileRpc(method: "profileStart" | "profileStop" | "profileDump"): Promise<string | null> {
        if (!this.socket) { console.warn(`profileRpc socket is null`); return null; }
        console.log(`snd profileRpc method=${method}`);
        const res = await this.socket.rpc(method);
        return res?.payload ?? null;
    }

    // ===== オセロ RPC =====

    /** オセロゲームを作成
     *  @param isCpu true を指定すると自作 CPU 対戦ゲームとして作成される（オーナーは観戦席のみ）
     *  @param cpuColor CPU 対戦時の CPU 席色。1=黒(CPU 先手, 既定)、2=白(CPU 後手)
     *  @param cpuName 自作 CPU の識別名（+VE 応答の name 部分）。ロビーコメント末尾に付与される */
    async othelloCreate(worldId: number, isCpu: boolean = false, cpuColor: 1 | 2 = 1, cpuName: string = ""): Promise<OthelloUpdatePayload | null> {
        if (!this.socket) return null;
        const cpuLog = isCpu ? ` isCpu cpuColor=${cpuColor}${cpuName ? ` cpuName="${cpuName}"` : ""}` : "";
        console.log(`snd othelloCreate worldId=${worldId}${cpuLog}`);
        const r = await this.socket.rpc("othelloCreate", JSON.stringify({ worldId, isCpu, cpuColor, cpuName }));
        return r?.payload ? JSON.parse(r.payload) as OthelloUpdatePayload : null;
    }

    /** オセロゲームに参加
     *  @param watch true で観戦取得（状態変更なし）
     *  @param withCpu true で自分のシリアル CPU で参加（CPU vs CPU 対戦に昇格させる）
     *  @param cpuName withCpu=true のとき、自分の CPU の +VE 識別名（プロトコルバージョン 2 桁を除く） */
    async othelloJoin(gameId: string, watch: boolean = false, withCpu: boolean = false, cpuName: string = ""): Promise<OthelloUpdatePayload | null> {
        if (!this.socket) return null;
        const flags = `${watch ? " watch" : ""}${withCpu ? " withCpu" : ""}${withCpu && cpuName ? ` cpuName="${cpuName}"` : ""}`;
        console.log(`snd othelloJoin gameId=${gameId}${flags}`);
        const r = await this.socket.rpc("othelloJoin", JSON.stringify({ gameId, watch, withCpu, cpuName }));
        return r?.payload ? JSON.parse(r.payload) as OthelloUpdatePayload : null;
    }

    /** オセロで石を置く */
    async othelloMove(gameId: string, row: number, col: number): Promise<OthelloUpdatePayload | null> {
        if (!this.socket) return null;
        console.log(`snd othelloMove gameId=${gameId} row=${row} col=${col}`);
        const r = await this.socket.rpc("othelloMove", JSON.stringify({ gameId, row, col }));
        return r?.payload ? JSON.parse(r.payload) as OthelloUpdatePayload : null;
    }

    /** オセロで投了 */
    async othelloResign(gameId: string): Promise<OthelloUpdatePayload | null> {
        if (!this.socket) return null;
        console.log(`snd othelloResign gameId=${gameId}`);
        const r = await this.socket.rpc("othelloResign", JSON.stringify({ gameId }));
        return r?.payload ? JSON.parse(r.payload) as OthelloUpdatePayload : null;
    }

    /** 待機中のオセロゲームをキャンセルする（作成者のみ） */
    async othelloCancel(gameId: string): Promise<void> {
        if (!this.socket) return;
        console.log(`snd othelloCancel gameId=${gameId}`);
        await this.socket.rpc("othelloCancel", JSON.stringify({ gameId }));
    }

    /** 待機中オセロのコメント列を更新する（作成者のみ） */
    async othelloComment(gameId: string, text: string): Promise<void> {
        if (!this.socket) return;
        console.log(`snd othelloComment gameId=${gameId} text=${text}`);
        try {
            await this.socket.rpc("othelloComment", JSON.stringify({ gameId, text }));
        } catch (e) {
            console.warn("othelloComment error:", e);
        }
    }

    /** 待機中ゲームのオーナーが指定ユーザへ招待通知を送る */
    async othelloInvite(gameId: string, targetId: string): Promise<void> {
        if (!this.socket) return;
        console.log(`snd othelloInvite gameId=${gameId} target=${targetId.slice(0, 8)}`);
        await this.socket.rpc("othelloInvite", JSON.stringify({ gameId, targetId }));
    }

    /** 招待を受けたユーザが NO を押したときにオーナーへ拒否通知を送る */
    async othelloInviteReject(inviterUid: string, gameNo: number): Promise<void> {
        if (!this.socket) return;
        console.log(`snd othelloInviteReject inviter=${inviterUid.slice(0, 8)} gameNo=${gameNo}`);
        try {
            await this.socket.rpc("othelloInviteReject", JSON.stringify({ inviterUid, gameNo }));
        } catch (e) {
            console.warn("othelloInviteReject error:", e);
        }
    }

    /** オセロ対戦履歴を取得する。socket 未接続時は null（呼び出し側は描画しない） */
    async othelloHistory(): Promise<OthelloHistoryRecord[] | null> {
        if (!this.socket) return null;
        console.log("snd othelloHistory");
        const r = await this.socket.rpc("othelloHistory", "");
        try {
            return JSON.parse(r.payload ?? "[]") as OthelloHistoryRecord[];
        } catch { return []; }
    }

    /** オセロ更新通知の購読/解除。送信できた場合 true を返す */
    async othelloSubscribe(subscribe: boolean): Promise<boolean> {
        if (!this.socket || !this.matchId) return false;
        console.log(`snd othelloSubscribe ${subscribe ? "subscribe" : "unsubscribe"}`);
        await this.socket.sendMatchState(this.matchId, OP_OTHELLO_SUB, this._encoder.encode(JSON.stringify({ subscribe })));
        return true;
    }

}
